// src/pages/Admin/Components/OrdersPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Search, ChevronRight, ChevronLeft, User, Package, Truck, Trash2, CheckCircle, Calendar, Clock, X, Loader2, Eye, ShoppingBag, CreditCard, MapPin, Phone, Mail
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { adminGetOrders, adminUpdateOrder, adminDeleteOrder } from '../../../services/adminServices';

const OrdersPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth) || {};
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const itemsPerPage = 10;
  const searchTimeoutRef = useRef(null);
  const searchDelayTimeoutRef = useRef(null);

  // ✅ استیت‌های مربوط به مودال ارسال سفارش
  const [shippingModalOpen, setShippingModalOpen] = useState(false);
  const [shippingOrderData, setShippingOrderData] = useState(null);
  const [shippingTrackingCode, setShippingTrackingCode] = useState('');
  const [shippingMethod, setShippingMethod] = useState('post'); // 'post' یا 'tipax'
  const [shippingError, setShippingError] = useState('');

  const fetchOrders = () => {
    if (!userInfo?.access) return;
    setLoading(true);
    adminGetOrders({ userInfo, dispatch })
      .then(res => {
        const mapped = res.data.map(order => ({
          ...order,
          id: `#${order._id}`,
          customer: order.user?.name || 'کاربر ناشناس',
          amount: `${(order.totalPrice || 0).toLocaleString()} تومان`,
          paymentStatus: order.isPaid ? 'پرداخت شده' : 'در انتظار',
          shippingStatus: order.isDelivered ? 'ارسال شده' : (order.isPaid ? 'در حال پردازش' : 'لغو شده'),
          shippingDate: order.createdAt ? new Date(order.createdAt).toLocaleDateString('fa-IR') : '---',
          items: (order.orderItems || []).map(item => ({
            name: item.name,
            qty: item.qty,
            price: item.price,
            total: item.qty * item.price,
            selected_color_name: item.selected_color_name || 'بدون رنگ',
            selected_color_code: item.selected_color_code || null,
            productId: item.product,
            image: item.image,
          })),
          userPhone: order.user?.phone || '---',
          userEmail: order.user?.email || '---',
          shippingAddress: order.shippingAddress,
          tracking_code: order.tracking_code || '',
          shipping_method: order.shipping_method || 'post',
        }));
        setOrders(mapped);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'خطا در دریافت سفارشات');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchOrders();
  }, [dispatch, userInfo]);

  const handleTogglePaid = (orderId, currentStatus) => {
    if (!window.confirm(`آیا از تغییر وضعیت پرداخت این سفارش مطمئن هستید؟`)) return;
    setActionLoading(orderId);
    adminUpdateOrder(orderId, { isPaid: !currentStatus }, { userInfo, dispatch })
      .then(() => fetchOrders())
      .catch(err => alert('خطا در تغییر وضعیت پرداخت'))
      .finally(() => setActionLoading(null));
  };

  // ✅ باز کردن مودال ارسال
  const handleOpenShippingModal = (order) => {
    setShippingOrderData(order);
    setShippingTrackingCode(order.tracking_code || '');
    setShippingMethod(order.shipping_method || 'post');
    setShippingError('');
    setShippingModalOpen(true);
  };

  // ✅ ثبت اطلاعات ارسال از مودال
  const handleSubmitShipping = () => {
    if (!shippingTrackingCode.trim()) {
      setShippingError('وارد کردن کد رهگیری الزامی است.');
      return;
    }
    setActionLoading(shippingOrderData._id);
    
    adminUpdateOrder(
      shippingOrderData._id, 
      { 
        isDelivered: true, 
        tracking_code: shippingTrackingCode,
        shipping_method: shippingMethod
      }, 
      { userInfo, dispatch }
    )
      .then(() => {
        fetchOrders();
        setShippingModalOpen(false);
      })
      .catch(err => {
        setShippingError('خطا در ثبت اطلاعات ارسال');
        console.error(err);
      })
      .finally(() => setActionLoading(null));
  };

  // ✅ لغو ارسال (برگرداندن به حالت قبل)
  const handleCancelDelivered = (orderId) => {
    if (!window.confirm(`آیا از لغو ارسال این سفارش مطمئن هستید؟`)) return;
    setActionLoading(orderId);
    adminUpdateOrder(orderId, { isDelivered: false }, { userInfo, dispatch })
      .then(() => fetchOrders())
      .catch(err => alert('خطا در لغو ارسال'))
      .finally(() => setActionLoading(null));
  };

  const handleDelete = (orderId) => {
    if (!window.confirm('آیا از حذف این سفارش مطمئن هستید؟ این عمل غیرقابل بازگشت است.')) return;
    setActionLoading(orderId);
    adminDeleteOrder(orderId, { userInfo, dispatch })
      .then(() => fetchOrders())
      .catch(err => alert('خطا در حذف سفارش'))
      .finally(() => setActionLoading(null));
  };

  const handleViewOrder = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const filteredOrders = orders.filter(order => {
    if (activeTab === 'all') return true;
    if (activeTab === 'processing') return order.shippingStatus === 'در حال پردازش';
    if (activeTab === 'shipped') return order.shippingStatus === 'ارسال شده';
    if (activeTab === 'cancelled') return order.shippingStatus === 'لغو شده';
    return true;
  });

  const handleSearch = (value) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchDelayTimeoutRef.current) clearTimeout(searchDelayTimeoutRef.current);
    setSearchTerm(value);
    if (!value.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      setCurrentPage(1);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      setIsSearching(true);
      searchDelayTimeoutRef.current = setTimeout(() => {
        const results = filteredOrders.filter(o =>
          o.id.includes(value.trim()) ||
          o.customer.includes(value.trim())
        );
        setSearchResults(results);
        setIsSearching(false);
        setCurrentPage(1);
      }, 500);
    }, 300);
  };

  const clearSearch = () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchDelayTimeoutRef.current) clearTimeout(searchDelayTimeoutRef.current);
    setSearchTerm('');
    setSearchResults([]);
    setIsSearching(false);
    setCurrentPage(1);
  };

  const currentData = searchTerm.trim() ? searchResults : filteredOrders;
  const totalItems = currentData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentItems = currentData.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getStatusBadge = (status) => {
    const baseClass = "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap border";
    switch(status) {
      case 'پرداخت شده':
        return <span className={`${baseClass} bg-green-50 text-green-700 border-green-200`}><CheckCircle className="w-3.5 h-3.5 text-green-500" /> {status}</span>;
      case 'در انتظار':
        return <span className={`${baseClass} bg-yellow-50 text-yellow-700 border-yellow-200`}><Clock className="w-3.5 h-3.5 text-yellow-500" /> {status}</span>;
      case 'ارسال شده':
        return <span className={`${baseClass} bg-blue-50 text-blue-700 border-blue-200`}><Truck className="w-3.5 h-3.5 text-blue-500" /> {status}</span>;
      case 'در حال پردازش':
        return <span className={`${baseClass} bg-amber-50 text-amber-700 border-amber-200`}><Clock className="w-3.5 h-3.5 text-amber-500" /> {status}</span>;
      case 'لغو شده':
        return <span className={`${baseClass} bg-red-50 text-red-700 border-red-200`}><X className="w-3.5 h-3.5 text-red-500" /> {status}</span>;
      default:
        return <span className={`${baseClass} bg-gray-100 text-gray-600 border-gray-200`}>-</span>;
    }
  };

  const SkeletonRow = () => (
    <tr className="animate-pulse border-b border-gray-100">
      <td className="px-6 py-5"><div className="h-5 w-32 bg-gray-200 rounded"></div></td>
      <td className="px-6 py-5 text-center"><div className="h-5 w-16 bg-gray-200 rounded mx-auto"></div></td>
      <td className="px-6 py-5 text-center"><div className="h-5 w-28 bg-gray-200 rounded mx-auto"></div></td>
      <td className="px-6 py-5 text-center"><div className="h-6 w-24 bg-gray-200 rounded mx-auto"></div></td>
      <td className="px-6 py-5 text-center"><div className="h-6 w-24 bg-gray-200 rounded mx-auto"></div></td>
      <td className="px-6 py-5 text-center"><div className="h-5 w-24 bg-gray-200 rounded mx-auto"></div></td>
      <td className="px-6 py-5"><div className="h-5 w-48 bg-gray-200 rounded"></div></td>
      <td className="px-6 py-5 text-center"><div className="h-16 w-16 bg-gray-200 rounded mx-auto"></div></td>
    </tr>
  );

  if (loading) {
    return (
      <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded-xl"></div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium">خطا: {error}</p>
          <button onClick={fetchOrders} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">تلاش مجدد</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen" dir="rtl">
      
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 border-b border-gray-200 pb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight mb-2">مدیریت سفارش‌ها</h1>
          <p className="text-sm text-gray-500">سفارش‌های جدید را بررسی و وضعیت ارسال را مدیریت کنید.</p>
        </div>
      </div>

      <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-2 w-full md:w-auto justify-center md:justify-start">
          {[{ id: 'all', label: 'همه سفارشات' }, { id: 'processing', label: 'در حال پردازش' }, { id: 'shipped', label: 'ارسال شده' }, { id: 'cancelled', label: 'لغو شده' }].map((filter) => (
            <button
              key={filter.id}
              onClick={() => { setActiveTab(filter.id); setCurrentPage(1); clearSearch(); }}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${activeTab === filter.id ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-72">
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {isSearching ? <Loader2 className="w-5 h-5 text-blue-500 animate-spin" /> : <Search className="w-5 h-5 text-gray-400" />}
          </div>
          <input 
            type="text" 
            placeholder="جستجو بر اساس کد یا نام مشتری..." 
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
          />
          {searchTerm && (
            <button onClick={clearSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-right text-sm font-bold text-gray-600 uppercase tracking-wider">
                <th className="px-6 py-4 text-right min-w-[130px]">مشتری</th>
                <th className="px-6 py-4 text-center min-w-[80px]">سفارش</th>
                <th className="px-6 py-4 text-center min-w-[120px]">مبلغ</th>
                <th className="px-6 py-4 text-center min-w-[120px]">پرداخت</th>
                <th className="px-6 py-4 text-center min-w-[120px]">ارسال</th>
                <th className="px-6 py-4 text-center min-w-[100px]">تاریخ</th>
                <th className="px-6 py-4 text-right min-w-[260px]">آیتم</th>
                <th className="px-6 py-4 text-center min-w-[100px] bg-gray-50 border-r border-gray-200">اقدامات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isSearching ? (
                Array(5).fill(0).map((_, i) => <SkeletonRow key={i} />)
              ) : currentItems.length > 0 ? (
                currentItems.map((order) => (
                  <tr key={order._id} className="hover:bg-blue-50/30 transition-colors duration-200 group">
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <span className="text-sm font-medium text-gray-800 whitespace-nowrap">{order.customer}</span>
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500"><User className="w-4 h-4" /></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-bold text-blue-600 whitespace-nowrap">{order.id}</td>
                    <td className="px-6 py-4 text-center text-sm font-medium text-gray-800 whitespace-nowrap">{order.amount}</td>
                    <td className="px-6 py-4 text-center">{getStatusBadge(order.paymentStatus)}</td>
                    <td className="px-6 py-4 text-center">{getStatusBadge(order.shippingStatus)}</td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center gap-2 text-sm text-gray-600 bg-white border border-gray-200 px-3 py-1.5 rounded-lg shadow-sm whitespace-nowrap">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {order.shippingDate}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right cursor-pointer" onClick={() => setSelectedOrder(order)}>
                      <div className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-3 py-1.5 -mx-3 transition-colors group/item">
                        <Package className="w-4 h-4 text-gray-400 group-hover/item:text-blue-500 transition-colors" />
                        <span className="text-sm text-gray-700 truncate max-w-[140px] group-hover/item:text-blue-600 transition-colors">{order.items[0]?.name || 'بدون آیتم'}</span>
                        <span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-2 py-0.5 rounded-full border border-gray-200 whitespace-nowrap">{order.items.length} عدد</span>
                        <Eye className="w-4 h-4 text-gray-300 group-hover/item:text-blue-500 transition-colors" />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center border-r border-gray-200 bg-gray-50/30">
                      <div className="flex flex-col gap-1.5 w-full min-w-[85px]">
                        <button
                          onClick={() => handleTogglePaid(order._id, order.isPaid)}
                          disabled={actionLoading === order._id}
                          className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors w-full ${order.isPaid ? 'bg-green-100 text-green-700 border border-green-200 hover:bg-green-200' : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'} disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {actionLoading === order._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                          {order.isPaid ? 'لغو پرداخت' : 'پرداخت'}
                        </button>

                        {order.isDelivered ? (
                          <button
                            onClick={() => handleCancelDelivered(order._id)}
                            disabled={actionLoading === order._id}
                            className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200 rounded hover:bg-blue-200 transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoading === order._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
                            لغو ارسال
                          </button>
                        ) : (
                          <button
                            onClick={() => handleOpenShippingModal(order)}
                            disabled={actionLoading === order._id}
                            className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoading === order._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
                            ارسال
                          </button>
                        )}

                        <button
                          onClick={() => handleDelete(order._id)}
                          disabled={actionLoading === order._id}
                          className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100 transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {actionLoading === order._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="8" className="text-center py-12 text-gray-500 font-medium">سفارشی با این مشخصات یافت نشد.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {!isSearching && totalItems > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 gap-4">
            <div className="text-sm text-gray-600 font-medium order-2 sm:order-1">
              نمایش <span className="font-bold text-gray-900">{startIndex + 1}</span> تا <span className="font-bold text-gray-900">{endIndex}</span> عدد از <span className="font-bold text-gray-900">{totalItems}</span> سفارش
            </div>
            <div className="flex items-center gap-2 order-1 sm:order-2 bg-white p-1 rounded-lg shadow-sm border border-gray-200">
              <button onClick={() => goToPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="w-9 h-9 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50 transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => goToPage(p)} className={`w-9 h-9 flex items-center justify-center rounded text-sm font-bold transition-colors ${currentPage === p ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'text-gray-600 hover:bg-gray-50'}`}>{p}</button>
              ))}
              <button onClick={() => goToPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="w-9 h-9 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50 transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ============================================================
          📋 مودال جزییات کامل سفارش
          ============================================================ */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 overflow-hidden" dir="rtl">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedOrder(null)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-gray-200 bg-white">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-blue-600" />
                جزییات سفارش {selectedOrder.id}
              </h3>
              <div className="flex items-center gap-3">
                <button onClick={() => handleViewOrder(selectedOrder._id)} className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
                  <Eye className="w-4 h-4 inline ml-1" />
                  مشاهده کامل
                </button>
                <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* اطلاعات مشتری */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  اطلاعات مشتری
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-400">نام:</span> <span className="font-medium text-gray-800 mr-2">{selectedOrder.customer}</span></div>
                  <div><span className="text-gray-400">شماره تلفن:</span> <span className="font-medium text-gray-800 mr-2">{selectedOrder.userPhone || '---'}</span></div>
                  <div className="col-span-2"><span className="text-gray-400">ایمیل:</span> <span className="font-medium text-gray-800 mr-2">{selectedOrder.userEmail || '---'}</span></div>
                </div>
              </div>

              {/* آدرس ارسال */}
              {selectedOrder.shippingAddress && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    آدرس ارسال
                  </h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p>{selectedOrder.shippingAddress.address || '---'}</p>
                    <p>{selectedOrder.shippingAddress.city || ''} {selectedOrder.shippingAddress.country || ''}</p>
                    {selectedOrder.shippingAddress.postalCode && <p className="text-gray-400 text-xs">کد پستی: {selectedOrder.shippingAddress.postalCode}</p>}
                  </div>
                </div>
              )}

              {/* لیست محصولات */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4 text-blue-600" />
                  محصولات سفارش
                </h4>
                <div className="space-y-3">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <img src={item.image || 'https://via.placeholder.com/60'} alt={item.name} className="w-16 h-16 rounded-lg object-cover bg-gray-100" onError={(e) => e.target.src = 'https://via.placeholder.com/60?text=No+Image'} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <h5 className="text-sm font-bold text-gray-800">{item.name}</h5>
                            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{item.qty} عدد</span>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-600">
                            {item.selected_color_code && (
                              <div className="flex items-center gap-1.5">
                                <span>رنگ:</span>
                                <span className="w-4 h-4 rounded-full border border-gray-300 inline-block" style={{ backgroundColor: item.selected_color_code }} />
                                <span>{item.selected_color_name}</span>
                              </div>
                            )}
                            <div>قیمت واحد: <span className="font-medium">{item.price?.toLocaleString()} تومان</span></div>
                            <div>جمع: <span className="font-bold text-blue-600">{item.total?.toLocaleString()} تومان</span></div>
                          </div>
                          {item.productId && (
                            <button onClick={() => navigate(`/product/${item.productId}`)} className="mt-2 text-xs text-blue-500 hover:underline">مشاهده محصول</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* خلاصه مالی */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  خلاصه مالی
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">جمع محصولات:</span><span className="font-medium">{(selectedOrder.totalPrice || 0).toLocaleString()} تومان</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">هزینه ارسال:</span><span className="font-medium">{(selectedOrder.shippingPrice || 0).toLocaleString()} تومان</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">مالیات:</span><span className="font-medium">{(selectedOrder.taxPrice || 0).toLocaleString()} تومان</span></div>
                  <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between font-bold text-base">
                    <span className="text-gray-800">مبلغ نهایی:</span>
                    <span className="text-blue-700">{(selectedOrder.totalPrice || 0).toLocaleString()} تومان</span>
                  </div>
                </div>
              </div>

              {/* وضعیت‌ها */}
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2"><span className="text-gray-500">وضعیت پرداخت:</span>{getStatusBadge(selectedOrder.paymentStatus)}</div>
                <div className="flex items-center gap-2"><span className="text-gray-500">وضعیت ارسال:</span>{getStatusBadge(selectedOrder.shippingStatus)}</div>
                <div className="flex items-center gap-2"><span className="text-gray-500">تاریخ ثبت:</span><span className="font-medium">{selectedOrder.shippingDate}</span></div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <button onClick={() => { setSelectedOrder(null); handleViewOrder(selectedOrder._id); }} className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-md flex items-center justify-center gap-2">
                  <Eye className="w-5 h-5" />
                  مشاهده کامل سفارش در صفحه اختصاصی
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          📦 مودال ثبت اطلاعات ارسال (کد رهگیری و روش ارسال)
          ============================================================ */}
      {shippingModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">ثبت اطلاعات ارسال</h2>
              <button onClick={() => setShippingModalOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            
            {shippingError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {shippingError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">روش ارسال</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="shippingMethod" value="post" checked={shippingMethod === 'post'} onChange={() => setShippingMethod('post')} className="w-4 h-4 text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm text-gray-700">پست</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="shippingMethod" value="tipax" checked={shippingMethod === 'tipax'} onChange={() => setShippingMethod('tipax')} className="w-4 h-4 text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm text-gray-700">تیپاکس</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">کد رهگیری پستی</label>
                <input 
                  type="text" 
                  value={shippingTrackingCode} 
                  onChange={(e) => setShippingTrackingCode(e.target.value)} 
                  placeholder="کد رهگیری را وارد کنید" 
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setShippingModalOpen(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">انصراف</button>
              <button onClick={handleSubmitShipping} disabled={actionLoading} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                ثبت و تغییر وضعیت
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;